import { useEffect, useMemo, useRef } from "react";
import { AlertTriangle, Play, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import type { PdfDocument, PdfTool, PdfToolParam } from "../types";

/** Wartości startowe z opisu narzędzia (backend jest źródłem prawdy o domyślnych). */
export function defaultParams(tool: PdfTool): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  tool.params.forEach((p) => {
    if (p.default !== undefined) out[p.name] = p.default;
    else if (p.type === "bool") out[p.name] = false;
    else if (p.type === "lines" || p.type === "cells") out[p.name] = [];
    else out[p.name] = "";
  });
  return out;
}

function visible(param: PdfToolParam, values: Record<string, unknown>): boolean {
  if (!param.when) return true;
  return Object.entries(param.when).every(([key, expected]) => String(values[key]) === String(expected));
}

export function missingRequired(tool: PdfTool, values: Record<string, unknown>): string[] {
  return tool.params
    .filter((p) => p.required && visible(p, values))
    .filter((p) => {
      const v = values[p.name];
      return v === "" || v === undefined || v === null || (Array.isArray(v) && v.length === 0);
    })
    .map((p) => p.label);
}

function Field({
  param,
  value,
  onChange,
  pagesHint,
  onUseSelectedPages,
}: {
  param: PdfToolParam;
  value: unknown;
  onChange: (v: unknown) => void;
  pagesHint?: string;
  onUseSelectedPages?: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const id = `pdf-param-${param.name}`;

  if (param.type === "bool") {
    return (
      <label htmlFor={id} className="flex cursor-pointer items-center gap-2 py-1 text-xs">
        <input
          id={id}
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="h-3.5 w-3.5 accent-[var(--color-primary)]"
        />
        <span className="text-[var(--color-text-secondary)]">{param.label}</span>
      </label>
    );
  }

  return (
    <div className="py-1">
      <label htmlFor={id} className="section-label mb-1 block">
        {param.label}
        {param.required && <span className="text-[var(--color-primary)]"> *</span>}
      </label>

      {/* Wiersze do dopisania: człowiek pisze je jeden pod drugim, numer strony
          po znaku „|” — układ (wcięcie, krój, kropki) robi za niego serwer. */}
      {param.type === "lines" && (
        <textarea
          id={id}
          rows={5}
          value={Array.isArray(value)
            ? (value as { text?: string; right?: string }[])
                .map((w) => (typeof w === "string" ? w : `${w.text ?? ""}${w.right ? ` | ${w.right}` : ""}`))
                .join("\n")
            : String(value ?? "")}
          placeholder={"Z1. Zaświadczenie Wójta | 31\nZ2. Wypis z rejestru gruntów | 34"}
          onChange={(e) =>
            onChange(
              e.target.value
                .split("\n")
                .map((w) => w.trim())
                .filter(Boolean)
                .map((w) => {
                  const i = w.lastIndexOf("|");
                  return i > 0
                    ? { text: w.slice(0, i).trim(), right: w.slice(i + 1).trim() }
                    : { text: w };
                }),
            )
          }
          className="input-base w-full py-1.5 text-xs"
        />
      )}

      {/* Komórki jednego wiersza tabeli, po jednej na kolumnę. */}
      {param.type === "cells" && (
        <input
          id={id}
          type="text"
          value={Array.isArray(value) ? (value as string[]).join(" | ") : String(value ?? "")}
          placeholder="ZWM | Zakładowa Wytyczna Montażu"
          onChange={(e) =>
            onChange(e.target.value.split("|").map((c) => c.trim()).filter(Boolean))
          }
          className="input-base w-full py-1.5 text-xs"
        />
      )}

      {param.type === "choice" && (
        <select
          id={id}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          className="input-base py-1.5 text-xs"
        >
          {(param.options ?? []).map((o) => (
            <option key={String(o.value)} value={String(o.value)}>
              {o.label}
            </option>
          ))}
        </select>
      )}

      {param.type === "color" && (
        <div className="flex items-center gap-2">
          <input
            id={id}
            type="color"
            value={String(value || "#dc1f1f")}
            onChange={(e) => onChange(e.target.value)}
            className="h-7 w-10 cursor-pointer border border-[var(--color-border)] bg-transparent p-0.5"
          />
          <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
            {String(value || "#dc1f1f")}
          </span>
        </div>
      )}

      {param.type === "image" && (
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            id={id}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => onChange(String(reader.result || ""));
              reader.readAsDataURL(file);
            }}
          />
          <Button type="button" variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
            <Upload size={13} className="mr-1.5" /> Wybierz obraz
          </Button>
          {typeof value === "string" && value.startsWith("data:") && (
            <>
              <img src={value} alt="Podgląd" className="h-7 border border-[var(--color-border)]" />
              <button
                type="button"
                onClick={() => onChange("")}
                aria-label="Usuń obraz"
                className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
              >
                <X size={13} />
              </button>
            </>
          )}
        </div>
      )}

      {["text", "pages", "password", "int", "float"].includes(param.type) && (
        <div className="flex items-center gap-1.5">
          <input
            id={id}
            type={param.type === "password" ? "password" : param.type === "text" || param.type === "pages" ? "text" : "number"}
            step={param.type === "float" ? "any" : undefined}
            min={param.min}
            value={String(value ?? "")}
            placeholder={param.placeholder ?? (param.type === "pages" ? "np. 1-3,7" : "")}
            onChange={(e) =>
              onChange(param.type === "int" || param.type === "float" ? e.target.value : e.target.value)
            }
            className="input-base py-1.5 text-xs"
          />
          {param.type === "pages" && onUseSelectedPages && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onUseSelectedPages}
              title="Wstaw strony zaznaczone na miniaturach"
              className="whitespace-nowrap"
            >
              Z zaznaczenia
            </Button>
          )}
        </div>
      )}

      {param.type === "pages" && pagesHint && (
        <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">{pagesHint}</p>
      )}
    </div>
  );
}

/**
 * Panel parametrów wybranego kafelka. Pokazuje TYLKO to, czego dane narzędzie
 * potrzebuje (pola sterowane polem `when` z rejestru operacji).
 */
export function ToolParams({
  tool,
  documents,
  selectedPages,
  values,
  onChange,
  onRun,
  onClose,
  running,
  error,
}: {
  tool: PdfTool;
  documents: PdfDocument[];
  selectedPages: number[];
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  onRun: () => void;
  onClose: () => void;
  running: boolean;
  error: string | null;
}) {
  const missing = useMemo(() => missingRequired(tool, values), [tool, values]);
  const shown = tool.params.filter((p) => p.type !== "shapes" && visible(p, values));

  // Zaznaczenie stron na miniaturach jest dla użytkownika oczywistym „wyborem
  // stron” — wstawiamy je od razu, żeby nie trzeba było przepisywać numerów.
  useEffect(() => {
    const pagesParam = tool.params.find((p) => p.type === "pages");
    if (!pagesParam || selectedPages.length === 0) return;
    if (values[pagesParam.name]) return;
    onChange({ ...values, [pagesParam.name]: selectedPages.join(",") });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool.name]);

  const set = (name: string, v: unknown) => onChange({ ...values, [name]: v });

  return (
    <div className="flex flex-col border-t border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="flex items-start justify-between gap-2 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2">
        <div className="min-w-0">
          <h3 className="truncate font-mono text-[0.7rem] font-bold uppercase tracking-widest text-[var(--color-text-primary)]">
            {tool.label}
          </h3>
          <p className="mt-0.5 truncate text-[11px] text-[var(--color-text-muted)]">{tool.hint}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Zamknij narzędzie"
          className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
        >
          <X size={15} />
        </button>
      </div>

      <div className="max-h-[38vh] overflow-y-auto px-3 py-2">
        <p className="mb-1 text-[11px] text-[var(--color-text-secondary)]">
          {documents.length === 0 ? (
            <span className="text-[var(--color-primary)]">Zaznacz plik na pulpicie.</span>
          ) : (
            <>
              Na pliku:{" "}
              <span className="font-medium text-[var(--color-text-primary)]">
                {documents.map((d) => d.name).join(", ")}
              </span>
            </>
          )}
        </p>

        {shown.map((p) => (
          <Field
            key={p.name}
            param={p}
            value={values[p.name]}
            onChange={(v) => set(p.name, v)}
            pagesHint={
              selectedPages.length > 0
                ? `Zaznaczone miniatury: ${selectedPages.join(", ")}`
                : "Puste = wszystkie strony"
            }
            onUseSelectedPages={
              selectedPages.length > 0 ? () => set(p.name, selectedPages.join(",")) : undefined
            }
          />
        ))}

        {shown.length === 0 && (
          <p className="py-1 text-[11px] text-[var(--color-text-muted)]">
            To narzędzie nie wymaga żadnych ustawień.
          </p>
        )}

        {error && (
          <p className="mt-2 flex items-start gap-1.5 border border-[var(--color-primary)] bg-[var(--color-primary-dim)] px-2 py-1.5 text-[11px] text-[var(--color-text-primary)]">
            <AlertTriangle size={13} className="mt-px shrink-0 text-[var(--color-primary)]" />
            <span>{error}</span>
          </p>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-[var(--color-border)] px-3 py-2">
        <span className="text-[11px] text-[var(--color-text-muted)]">
          {missing.length > 0 ? `Uzupełnij: ${missing.join(", ")}` : tool.heavy ? "Może policzyć się w tle" : " "}
        </span>
        <Button
          type="button"
          size="sm"
          onClick={onRun}
          loading={running}
          disabled={documents.length === 0 || missing.length > 0}
        >
          <Play size={13} className="mr-1.5" /> Wykonaj
        </Button>
      </div>
    </div>
  );
}
